/**
 * ThemedBadge Component
 * 
 * Badge/tag component that adapts its appearance based on the active immersive theme.
 * Supports all 5 themes with unique visual characteristics.
 */

import React from 'react';
import { useImmersiveTheme } from '../../hooks/useImmersiveTheme';
import { ImmersiveThemeType, ThemedBadgeProps } from '../../types/immersive-theme.types';
import {
  neumorphismBadge,
  neumorphismBadgeSuccess,
  neumorphismBadgeWarning,
  neumorphismBadgeDanger,
} from './theme-styles/neumorphism';
import {
  brutalistBadge,
  brutalistBadgeSuccess,
  brutalistBadgeWarning,
  brutalistBadgeDanger,
  brutalistBadgeInfo,
} from './theme-styles/brutalist';
import {
  retroTerminalBadge,
  retroTerminalBadgeSuccess,
  retroTerminalBadgeWarning,
  retroTerminalBadgeDanger,
  retroTerminalBadgeInfo,
} from './theme-styles/retro-terminal';
import {
  organicNatureBadge,
  organicNatureBadgeSuccess,
  organicNatureBadgeWarning,
  organicNatureBadgeDanger,
  organicNatureBadgeInfo,
  organicNatureBadgeOutline,
} from './theme-styles/organic-nature';
import {
  luxuryGoldBadge,
  luxuryGoldBadgeSuccess,
  luxuryGoldBadgeWarning,
  luxuryGoldBadgeDanger,
  luxuryGoldBadgeInfo,
  luxuryGoldBadgeOutline,
} from './theme-styles/luxury-gold';

// Base badge styles per theme
const badgeStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 10px',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '999px',
    background: '#e5e7eb',
    color: '#374151',
  },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismBadge,
  [ImmersiveThemeType.BRUTALIST]: brutalistBadge,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalBadge,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureBadge,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldBadge,
};

// Variant styles per theme
const getVariantStyles = (theme: ImmersiveThemeType, variant: string): React.CSSProperties => {
  const variantMap: Record<ImmersiveThemeType, Record<string, React.CSSProperties>> = {
    [ImmersiveThemeType.NONE]: {
      default: { background: '#e5e7eb', color: '#374151' },
      success: { background: '#dcfce7', color: '#166534' },
      warning: { background: '#fef3c7', color: '#92400e' },
      error: { background: '#fee2e2', color: '#991b1b' },
      info: { background: '#dbeafe', color: '#1e40af' },
      primary: { background: '#3b82f6', color: '#ffffff' },
    },
    [ImmersiveThemeType.NEUMORPHISM]: {
      default: {},
      success: neumorphismBadgeSuccess,
      warning: neumorphismBadgeWarning,
      error: neumorphismBadgeDanger,
      info: { background: 'var(--neu-info)', color: '#ffffff' },
      primary: { background: 'var(--neu-primary)', color: '#ffffff' },
    },
    [ImmersiveThemeType.BRUTALIST]: {
      default: {},
      success: brutalistBadgeSuccess,
      warning: brutalistBadgeWarning,
      error: brutalistBadgeDanger,
      info: brutalistBadgeInfo,
      primary: { background: 'var(--brut-text)', color: 'var(--brut-bg)' },
    },
    [ImmersiveThemeType.RETRO_TERMINAL]: {
      default: {},
      success: retroTerminalBadgeSuccess,
      warning: retroTerminalBadgeWarning,
      error: retroTerminalBadgeDanger,
      info: retroTerminalBadgeInfo,
      primary: { background: 'var(--term-text)', color: 'var(--term-bg)', textShadow: 'none' },
    },
    [ImmersiveThemeType.ORGANIC_NATURE]: {
      default: {},
      success: organicNatureBadgeSuccess,
      warning: organicNatureBadgeWarning,
      error: organicNatureBadgeDanger,
      info: organicNatureBadgeInfo,
      primary: { background: 'var(--org-primary)', color: '#ffffff' },
    },
    [ImmersiveThemeType.LUXURY_GOLD]: {
      default: {},
      success: luxuryGoldBadgeSuccess,
      warning: luxuryGoldBadgeWarning,
      error: luxuryGoldBadgeDanger,
      info: luxuryGoldBadgeInfo,
      primary: { background: 'var(--lux-gold)', color: 'var(--lux-bg)' },
    },
  };

  return variantMap[theme][variant] || {};
};

// Size configurations
const sizeConfig: Record<string, React.CSSProperties> = {
  sm: { padding: '2px 6px', fontSize: '10px' },
  md: { padding: '4px 10px', fontSize: '12px' },
  lg: { padding: '6px 14px', fontSize: '14px' },
};

// Outline styles per theme
const getOutlineStyles = (theme: ImmersiveThemeType, variant: string): React.CSSProperties => {
  const baseOutline: React.CSSProperties = {
    background: 'transparent',
    border: '1px solid currentColor',
  };

  const outlineColors: Record<string, string> = {
    default: theme === ImmersiveThemeType.RETRO_TERMINAL ? 'var(--term-text)' : '#6b7280',
    success: theme === ImmersiveThemeType.RETRO_TERMINAL ? '#00ff00' : '#22c55e',
    warning: theme === ImmersiveThemeType.RETRO_TERMINAL ? '#ffff00' : '#f59e0b',
    error: theme === ImmersiveThemeType.RETRO_TERMINAL ? '#ff4444' : '#ef4444',
    info: theme === ImmersiveThemeType.RETRO_TERMINAL ? '#00ffff' : '#3b82f6',
    primary: theme === ImmersiveThemeType.LUXURY_GOLD ? 'var(--lux-gold-solid)' : '#3b82f6',
  };

  return {
    ...baseOutline,
    color: outlineColors[variant] || outlineColors.default,
    borderColor: outlineColors[variant] || outlineColors.default,
    ...(theme === ImmersiveThemeType.RETRO_TERMINAL ? { textShadow: `0 0 10px ${outlineColors[variant]}` } : {}),
    ...(theme === ImmersiveThemeType.ORGANIC_NATURE ? organicNatureBadgeOutline : {}),
    ...(theme === ImmersiveThemeType.LUXURY_GOLD ? luxuryGoldBadgeOutline : {}),
  };
};

export const ThemedBadge: React.FC<ThemedBadgeProps> = ({
  variant = 'default',
  size = 'md',
  rounded = true,
  dot = false,
  outline = false,
  className,
  children,
}) => {
  const { theme, reducedMotion } = useImmersiveTheme();

  // Compute styles
  const computedStyles: React.CSSProperties = {
    ...badgeStyles[theme],
    ...sizeConfig[size],
    ...getVariantStyles(theme, variant),
    ...(outline ? getOutlineStyles(theme, variant) : {}),
    ...(rounded ? {} : { borderRadius: theme === ImmersiveThemeType.BRUTALIST ? '0' : '4px' }),
    ...(reducedMotion ? { transition: 'none' } : {}),
    display: 'inline-flex',
    alignItems: 'center',
    gap: dot ? '6px' : '0',
  };

  // Dot styles
  const dotStyles: React.CSSProperties = {
    width: size === 'sm' ? '6px' : size === 'lg' ? '10px' : '8px',
    height: size === 'sm' ? '6px' : size === 'lg' ? '10px' : '8px',
    borderRadius: '50%',
    background: 'currentColor',
    flexShrink: 0,
    ...(theme === ImmersiveThemeType.RETRO_TERMINAL ? { 
      boxShadow: '0 0 6px currentColor',
      animation: reducedMotion ? 'none' : 'pulse 2s ease-in-out infinite',
    } : {}),
  };

  return (
    <span style={computedStyles} className={className}>
      {theme === ImmersiveThemeType.RETRO_TERMINAL && (
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }`}</style>
      )}
      
      {dot && <span style={dotStyles} aria-hidden="true" />}
      
      {/* Terminal-style brackets for RETRO_TERMINAL */}
      {theme === ImmersiveThemeType.RETRO_TERMINAL && !dot && (
        <span style={{ opacity: 0.6 }}>[</span>
      )}
      
      <span>{children}</span>
      
      {theme === ImmersiveThemeType.RETRO_TERMINAL && !dot && (
        <span style={{ opacity: 0.6 }}>]</span>
      )}
    </span>
  );
};

export default ThemedBadge;
